import React from "react";

/**
 * The Markdown subset help articles are written in (/bo/help-center), rendered as React elements —
 * never as HTML, so an article cannot inject markup. Blocks: `#`/`##`/`###` headings, `-`/`*` and
 * `1.` lists, `>` notes, blank-line paragraphs. Inline: **bold**, *italic*, `code`, [links](https://…).
 */

const INLINE_RE = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`|\[[^\]]+\]\((?:https?:\/\/|\/|mailto:)[^)\s]+\))/g;

function inline(text: string): React.ReactNode[] {
    return text.split(INLINE_RE).map((part, i) => {
        // Bold and italic are parsed again inside, so **[a link](…)** stays a link
        if (part.startsWith("**") && part.endsWith("**")) return <strong key={i} className="font-semibold">{inline(part.slice(2, -2))}</strong>;
        if (part.startsWith("`") && part.endsWith("`")) return <code key={i} className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.9em]">{part.slice(1, -1)}</code>;
        const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (link) {
            const external = /^https?:/.test(link[2]);
            return (
                <a key={i} href={link[2]} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="font-medium underline underline-offset-2">
                    {link[1]}
                </a>
            );
        }
        if (part.length > 2 && part.startsWith("*") && part.endsWith("*")) return <em key={i}>{inline(part.slice(1, -1))}</em>;
        return part;
    });
}

export default function HelpMarkdown({ source }: { source: string }) {
    const blocks: React.ReactNode[] = [];
    const lines = source.replace(/\r\n/g, "\n").split("\n");
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];
        if (!line.trim()) {
            i++;
            continue;
        }
        const heading = line.match(/^(#{1,3})\s+(.*)$/);
        if (heading) {
            const level = heading[1].length;
            const className = level === 1 ? "text-lg font-semibold" : level === 2 ? "text-base font-semibold" : "text-sm font-semibold";
            blocks.push(<p key={i} className={`${className} mt-2 text-ink`}>{inline(heading[2])}</p>);
            i++;
            continue;
        }
        if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
            const ordered = /^\s*\d+\./.test(line);
            const items: string[] = [];
            while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^\s*([-*]|\d+\.)\s+/, ""));
                i++;
            }
            const ListTag = ordered ? "ol" : "ul";
            blocks.push(
                <ListTag key={i} className={`space-y-1 pl-5 ${ordered ? "list-decimal" : "list-disc"}`}>
                    {items.map((item, j) => (
                        <li key={j}>{inline(item)}</li>
                    ))}
                </ListTag>
            );
            continue;
        }
        if (line.startsWith(">")) {
            const quote: string[] = [];
            while (i < lines.length && lines[i].startsWith(">")) {
                quote.push(lines[i].replace(/^>\s?/, ""));
                i++;
            }
            blocks.push(
                <div key={i} className="rounded-2xl bg-gray-100 px-4 py-3">
                    {inline(quote.join(" "))}
                </div>
            );
            continue;
        }
        const paragraph: string[] = [];
        while (i < lines.length && lines[i].trim() && !/^(#{1,3}\s|>|\s*([-*]|\d+\.)\s+)/.test(lines[i])) {
            paragraph.push(lines[i]);
            i++;
        }
        blocks.push(
            <p key={i} className="whitespace-pre-line">
                {inline(paragraph.join("\n"))}
            </p>
        );
    }

    return <div className="space-y-3 text-sm leading-relaxed text-ink">{blocks}</div>;
}
